import React, { createContext, useState, useContext, useEffect } from 'react';
import { apiHandler } from '@/Services/apiHandler';
import toast from 'react-hot-toast';
import { useRouter } from 'next/navigation';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const router = useRouter();
    const { t } = useTranslation()

    const fetchUser = async () => {
        const token = getToken()
        if (!token) return
        try {
            // Check if user data is stored in localStorage (from temporary login)
            if (typeof window !== 'undefined') {
                const storedUser = localStorage.getItem('user');
                if (storedUser) {
                    try {
                        setUser(JSON.parse(storedUser));
                        return;
                    } catch (e) {
                        // If parsing fails, use static user
                    }
                }
            }
            // Use static data instead of API call
            const { staticUser } = await import('@/data/staticData');
            setUser(staticUser);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    };

    const register = async (username,
        phone,
        password,
        confirmPassword,) => {
        setLoading(true);
        try {
            // Use static data - simulate successful registration
            await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
            toast.success(t("alerts.registerSuccess"));
            router.push('/Mobile/auth/otp-verfiy'); // Redirect to dashboard or any protected route
        } catch (error) {
            console.error('Error registering:', error);
            toast.error(error?.response?.data[0] || t("alerts.registerError"));
        } finally {
            setLoading(false);
        }
    };

    const signIn = async (phone, password) => {
        setLoading(true);
        try {
            // Temporary login system - works without backend
            const { testUsers, staticUser } = await import('@/data/staticData');
            
            // Remove + and spaces from phone for comparison
            const cleanPhone = phone.replace(/[+\s]/g, '');
            
            // Find matching test user
            let matchedUser = testUsers.find(
                testUser => 
                    testUser.phone === cleanPhone || 
                    testUser.phone === phone ||
                    cleanPhone.includes(testUser.phone) ||
                    testUser.phone === "demo" // Allow demo login with any phone
            );
            
            // If password matches or is "demo", allow login
            if (matchedUser && (matchedUser.password === password || password === "demo" || password === "123456")) {
                // Generate a mock token
                const mockToken = `mock-token-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                
                // Set user data
                const userData = matchedUser.user || staticUser;
                setUser(userData);
                
                // Save token
                Cookies.set('token', mockToken, {
                    expires: 60, // 1 day
                    secure: process.env.NODE_ENV === 'production',
                    sameSite: 'strict',
                });
    
                if (typeof window !== 'undefined') {
                    localStorage.setItem('token', mockToken);
                    localStorage.setItem('user', JSON.stringify(userData));
                }
    
                toast.success(t("alerts.login") || "تم تسجيل الدخول بنجاح");
    
                await router.push('/');
    
                setTimeout(() => {
                    if (typeof window !== 'undefined') {
                        window.location.reload();
                    }
                }, 100);
            } else {
                // Try with any password for demo mode
                if (password && password.length >= 3) {
                    const mockToken = `mock-token-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                    const userData = staticUser;
                    setUser(userData);
                    
                    Cookies.set('token', mockToken, {
                        expires: 60,
                        secure: process.env.NODE_ENV === 'production',
                        sameSite: 'strict',
                    });
        
                    if (typeof window !== 'undefined') {
                        localStorage.setItem('token', mockToken);
                        localStorage.setItem('user', JSON.stringify(userData));
                    }
        
                    toast.success(t("alerts.login") || "تم تسجيل الدخول بنجاح (وضع تجريبي)");
                    await router.push('/');
                    
                    setTimeout(() => {
                        if (typeof window !== 'undefined') {
                            window.location.reload();
                        }
                    }, 100);
                } else {
                    toast.error(t("alerts.loginError") || "رقم الهاتف أو كلمة المرور غير صحيحة");
                }
            }
    
        } catch (error) {
            console.error('Error signing in:', error);
            toast.error(t("alerts.loginError") || "حدث خطأ أثناء تسجيل الدخول");
        } finally {
            setLoading(false);
        }
    };
    

    const OTPVerfiy = async ({ otp, phone }) => {
        setLoading(true);
        try {
            const response = await apiHandler(`/auth/verify-otp`, "post", { otp, phone })
            toast.success(t("alerts.OTPSucess"));
            router.push('/auth/login?message=register_success');
        } catch (error) {
            toast.error(t("alerts.OTPError"));
        } finally {
            setLoading(false);
        }
    }

    const getToken = () => {
        const token = Cookies.get('token');
        if (token) {
            return token;
        } else {
            return null; // Or handle the case where no token is available
        }
    };
    // Mock sign-out function
    const logOut = async () => {
        try {
            setUser(null);
            Cookies.remove('token', { path: '/' });
            if (typeof window !== 'undefined') {
                localStorage.removeItem('token');
            }
            window.location.reload()
            toast.success(t("alerts.logout"));
            getToken()
            router.push('/');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };
    // Mock authentication state persistence (replace with real logic)
    useEffect(() => {
        fetchUser();
    }, []);


    // Provide context values
    return (
        <AuthContext.Provider value={{ user, signIn, logOut, loading, register, OTPVerfiy, getToken }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook for accessing the AuthContext
export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
